import React, { useContext } from 'react';
import { useLoaderData } from 'react-router';

// Components
import AdditionalInformation from '@@components/AdditionalInformation/AdditionalInformation';
import DataError, { TErrorCode } from '@@components/DataError/DataError';
import JewelryGradingReport from '@@components/JewelryGradingReport/JewelryGradingReport';
import StandardGradingReport from '@@components/StandardGradingReport/StandardGradingReport';
import ReportHeader from '@@components/ReportHeader/ReportHeader';
import ReportSections from '@@components/ReportSections/ReportSections';

// Types and utilities
import {
    isJewelryReport,
    isStandardGradingReport,
    isStructuredReport,
    TJewelryReportData,
    TLabels,
    TReportData,
    TReportTypeCode,
    TStructuredReportData,
} from '@@config/report';
import { TLocale } from '@@config/locale';
import { LocaleContext, TLocaleContext } from '@@contexts/LocaleContext';
import { getAWSEnv, getElectronicReportURL, getReportCheckLandingURL, pushEvent } from '@@utils/utils.ts';

// Define loader data type
type LoaderData = {
    reportData: TReportData | TJewelryReportData | TStructuredReportData;
    labels: TLabels;
    error?: { code: TErrorCode; message: string };
    locale: TLocale;
    reportno?: string;
};

const Results: React.FC = () => {
    const { locale }: TLocaleContext = useContext(LocaleContext);
    const loaderData = useLoaderData() as LoaderData;
    const { reportData, labels, error, reportno: requestedReportNo } = loaderData;

    // Handle errors
    if (error?.code === '401') {
        window.location.href = getReportCheckLandingURL(requestedReportNo, locale);
        return null;
    }

    if (error) {
        return <DataError errorCode={error.code} fallbackMessage={error.message} />;
    }

    // Push analytics events in production
    if (getAWSEnv() === 'prod') {
        pushEvent('reportType', reportData.REPORT_TYPE ?? '');
        pushEvent('reportDate', reportData.REPORT_DATE ?? '');
    }

    // Common data preparation regardless of report type

    const reportNo: string = reportData.REPORT_NUMBER;
    const reportTypeCode: TReportTypeCode = reportData.REPORT_TYPE_CODE;

    // Determine primary CTA (Call To Action) URL
    let primaryCTA: URL | undefined = undefined;

    if (isStandardGradingReport(reportData) && reportTypeCode === 'DER') {
        primaryCTA = getElectronicReportURL(reportNo, reportData?.WEIGHT ?? '');
    } else if (reportData.HEADER?.PDF_URL?.VALUE) {
        primaryCTA = new URL(reportData.HEADER['PDF_URL'].VALUE);
    }

    return (
        <main className='container relative my-6' data-report-type={reportTypeCode}>
            {/* Single ReportHeader component for ALL report types */}
            <ReportHeader
                header={reportData.HEADER}
                reportTypeCode={reportTypeCode}
                reportNumber={reportNo}
                primaryCTA={primaryCTA}
                labels={labels}
                // Props for standard reports (will be ignored by structured/jewelry reports)
                reportDAC={isStandardGradingReport(reportData) ? (reportData.DIGITAL_CARD ?? '') : undefined}
                reportDTL={isStandardGradingReport(reportData) ? (reportData.DTL_PDF_URL ?? '') : undefined}
                reportDTLP={isStandardGradingReport(reportData) ? (reportData.DTLP_PDF_URL ?? '') : undefined}
                reportDTTL={isStandardGradingReport(reportData) ? (reportData.DTTL_PDF_URL ?? '') : undefined}
                reportPCL={isStandardGradingReport(reportData) ? (reportData.POS_CASE_LABEL ?? '') : undefined}
            />

            <hr className='dotted-double sm:mt-1 md:mt-4' />

            {isStructuredReport(reportData) && (
                <div className='flex flex-wrap'>
                    <div className='w-full'>
                        <div className='flex flex-wrap'>
                            <ReportSections sections={reportData.SECTIONS} />
                        </div>
                    </div>
                </div>
            )}

            {isJewelryReport(reportData) && (
                <JewelryGradingReport data={reportData} labels={labels} reportTypeCode={reportTypeCode} />
            )}

            {isStandardGradingReport(reportData) && (
                <StandardGradingReport
                    reportNumber={reportNo}
                    primaryCTA={primaryCTA}
                    data={reportData}
                    labels={labels}
                />
            )}

            {!isStandardGradingReport(reportData) && <AdditionalInformation reportTypeCode={reportTypeCode} />}
            <hr className='dotted sm:mt-1 md:mt-12' />
        </main>
    );
};

export default Results;
